// eslint-disable-next-line no-unused-vars
import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, number, shape } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  DatePicker,
  DatePickerInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';

import useNumberUtils from 'Hooks/useNumberUtils';
import ReportsPagination from 'Containers/Reports/ReportsPagination';
import { NWNotification } from 'Components/shared';

import IconCsv from 'Assets/images/csv.svg';

const statusColors = {
  PENDING: '#813d94',
  PROCESSING: '#1893d0',
  COMPLETED: '#01a982',
  FAILED: '#d52b1e',
  EXPIRED: '#999',
};

const MIN_STATEMENT_DATE = '2024-12-31';

const formatDateValue = date => {
  let day = date.getDate();
  let month = date.getMonth() + 1;
  const year = date.getFullYear();

  day = (day < 10) ? `0${ day }` : day;
  month = (month < 10) ? `0${ month }` : month;

  return `${ year }-${ month }-${ day }`;
};

const clampDateValue = (value, minDate, maxDate) => {
  if (value < minDate) {
    return minDate;
  }

  if (value > maxDate) {
    return maxDate;
  }

  return value;
};

const StyledAccountStatements = styled.div`
  .statementFilter {
    display: flex;
    flex-direction: column;
    padding: 25px 0;
    gap: 20px;

    .filters {
      display: flex;
      align-items: flex-end;
      gap: 10px;

      > div {
        flex: 1;
        min-width: 0;

        .cds--date-picker.cds--date-picker--single .cds--date-picker__input {
          width: 100%;
        }
      }

      button.cds--btn {
        height: 40px;
        max-width: none;
        min-width: 230px;
        justify-content: center;
        font-size: 1rem;
        text-align: center;
      }
    }

    .cds--date-picker,
    .cds--date-picker-container {
      width: 100%;

      .cds--date-picker-input__wrapper span {
        width: 100%;
      }
    }

    .cds--label {
      display: none;
    }

    .cds--date-picker__input {
      background: #fff;
      border: 1px solid #e2e2e2;
      border-radius: 5px;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: center;

    button {
      padding: 7px 3px;
      border: none;
      background: transparent;
      cursor: pointer;
      min-width: 30px;
      min-height: 31px;

      svg {
        width: 24px;
        height: 30px;
      }
    }
  }

  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead th {
      color: #3a3a3a;
      font-size: 16px;
      font-weight: 600;
      background: #f5f7f9;

      .cds--table-header-label {
        font-size: 16px;
        font-weight: 500;
        text-align: center;
      }
    }

    tbody tr td {
      background-color: #fff;
      text-align: center;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .statementFilter {      
      .filters {
        flex-direction: column;
        gap: 10px;

        > div {
          width: 100%;
          flex: none;
          padding: 0;
        }

        .cds--date-picker,
        .cds--date-picker-container,
        .cds--date-picker-input__wrapper,
        .cds--date-picker-input__wrapper span,
        .cds--date-picker__input {
          width: 100%;
        }

        button.cds--btn {
          width: 100%;
          max-width: none;
        }
      }
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;
          border: 1px solid #e2e2e2;
          position: relative;

          td {
            border: transparent;
            text-align: left;

            &:first-of-type {
              background: #F5F7F9;
              position: relative;
            }
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="period"],
    &[data-label="requestedAt"],
    &[data-label="status"],
    &[data-label="actions"] {
      display: flex;
      align-items: center;
      min-height: 40px;
      padding-left: 175px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 160px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  }
`;

const StyledStatus = styled.span`
  color: ${ props => statusColors[props.statusId] || 'inherit' };
  font-weight: 600;
`;

const AccountStatementsComponent = ({
  statements,
  loading,
  requestLoading,
  pagination,
  errors,
  hasActiveStatement,
  onRequestStatement,
  onDownload,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate } = useNumberUtils();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const maxStatementDate = formatDateValue(new Date());

  const headerData = useMemo(
    () => [
      {
        key: 'period',
        header: t('reports.accountStatements.period'),
      },
      {
        key: 'requestedAt',
        header: t('reports.accountStatements.requestedAt'),
      },
      {
        key: 'status',
        header: t('reports.accountStatements.status'),
      },
      {
        key: 'actions',
        header: t('pages.actions'),
      }
    ],
    []
  );

  const statementNotification = hasActiveStatement
    ? t('reports.accountStatements.activeRequestInfo')
    : t('reports.accountStatements.requestInfo');

  const handleDateChange = (setter, date, minDate, maxDate) => {
    if (!date || !date.length) {
      setter('');
      return;
    }

    setter(clampDateValue(formatDateValue(new Date(date)), minDate, maxDate));
  };

  const handleSubmit = () => {
    onRequestStatement({ startDate, endDate }).then(success => {
      if (success) {
        setStartDate('');
        setEndDate('');
      }
    });
  };

  const mapHeaderToLabel = label => t(`reports.accountStatements.${ label }`);

  const renderPeriod = value => {
    if (value?.label) {
      return value.label;
    }

    if (value?.startDate && value?.endDate) {
      return `${ formatDate(value.startDate, { timeStyle: undefined }) } - ${ formatDate(value.endDate, { timeStyle: undefined }) }`;
    }

    return '-';
  };

  const renderCell = cell => {
    if (cell.info.header === 'period') {
      return renderPeriod(cell.value);
    }

    if (cell.info.header === 'requestedAt') {
      return cell.value ? formatDate(cell.value) : '-';
    }

    if (cell.info.header === 'status') {
      return (
        <StyledStatus statusId={ cell.value }>
          { t(`reports.accountStatements.statuses.${ cell.value }`, cell.value) }
        </StyledStatus>
      );
    }

    if (cell.info.header === 'actions') {
      const { id, status } = cell.value;

      if (status === 'PENDING' || status === 'PROCESSING' || status === 'FAILED' || status === 'EXPIRED') {
        return null;
      }

      return (
        <div className="actions">
          <button type="button" onClick={ () => onDownload(id) }>
            <IconCsv />
          </button>
        </div>
      );
    }

    return cell.value;
  };

  return (
    <StyledAccountStatements theme={ theme }>
      <div className="statementFilter">
        <div className="filters">
          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            value={ startDate }
            minDate={ MIN_STATEMENT_DATE }
            maxDate={ endDate || maxStatementDate }
            onChange={ date => handleDateChange(setStartDate, date, MIN_STATEMENT_DATE, endDate || maxStatementDate) }
          >
            <DatePickerInput
              autoComplete="none"
              placeholder={ t('orders.dateFrom') }
              labelText={ t('orders.dateFrom') }
              id="account-statement-start-date"
              size="md"
            />
          </DatePicker>
          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            value={ endDate }
            minDate={ startDate || MIN_STATEMENT_DATE }
            maxDate={ maxStatementDate }
            onChange={ date => handleDateChange(setEndDate, date, startDate || MIN_STATEMENT_DATE, maxStatementDate) }
          >
            <DatePickerInput
              autoComplete="none"
              placeholder={ t('orders.dateTo') }
              labelText={ t('orders.dateTo') }
              id="account-statement-end-date"
              size="md"
            />
          </DatePicker>
          <Button
            kind="danger"
            disabled={ !startDate || !endDate || hasActiveStatement || requestLoading }
            onClick={ handleSubmit }
          >
            { t('reports.accountStatements.requestStatement') }
          </Button>
        </div>
        <NWNotification errors={ [{ type: 'info', message: statementNotification }] } />
        <NWNotification errors={ errors } delay={ 10 } />
      </div>
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ statements } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader { ...getHeaderProps({ header }) }>
                        { header.header }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { statements.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  { rows.map(row => (
                    <TableRow key={ row.id }>
                      { row.cells.map(cell => (
                        <StyledTableCell
                          data-label={ cell.info.header }
                          name={ mapHeaderToLabel(cell.info.header) }
                          key={ cell.id }
                        >
                          { renderCell(cell) }
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ReportsPagination
        loading={ loading }
        itemsName={ t('reports.accountStatements.paginationLabel') }
        { ...pagination }
      />
    </StyledAccountStatements>
  );
};

AccountStatementsComponent.propTypes = {
  statements: array,
  loading: bool,
  requestLoading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  errors: array,
  hasActiveStatement: bool,
  onRequestStatement: func,
  onDownload: func,
};

AccountStatementsComponent.displayName = 'AccountStatementsComponent';

export default AccountStatementsComponent;
